import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { api, onUnauthenticated } from '@/lib/api';
import { setSplashMessage } from '@/lib/splash';
import { isNetworkError } from '@/queries/client';
import { keys } from '@/queries/keys';
import { useBootstrap } from '@/queries/hooks';
import { PairScreen } from './PairScreen';
import { DisplayShell } from './Shell';

type Auth = 'checking' | 'unpaired' | 'ready' | 'offline';

export function DisplayApp() {
  const qc = useQueryClient();
  const [auth, setAuth] = useState<Auth>('checking');

  const check = useCallback(() => {
    api.http
      .refresh()
      .then((ok) => setAuth(ok ? 'ready' : 'unpaired'))
      // No network on launch: show cached data if we have any.
      .catch((e: unknown) => setAuth(isNetworkError(e) && qc.getQueryData(keys.bootstrap()) ? 'ready' : 'offline'));
  }, [qc]);

  useEffect(check, [check]);
  useEffect(() => onUnauthenticated(() => setAuth('unpaired')), []);
  useEffect(() => {
    if (auth !== 'offline') return;
    const t = setTimeout(check, 10_000);
    return () => clearTimeout(t);
  }, [auth, check]);

  const boot = useBootstrap(auth === 'ready');
  // Relaunch (iOS kills Home Screen apps freely): paint the cached home immediately while the
  // session refreshes in the background, instead of a splash on every launch.
  const cached = boot.data && boot.data.preferences && boot.data.device ? boot.data : undefined;
  if ((auth === 'checking' || auth === 'offline') && cached && cached.preferences) {
    return <DisplayShell boot={cached} prefs={cached.preferences} />;
  }

  if (auth === 'unpaired') {
    return (
      <PairScreen
        onPaired={() => {
          qc.clear();
          setAuth('ready');
        }}
      />
    );
  }
  if (auth === 'offline') return <Splash text="Can’t reach OurBeachVila. Retrying…" />;
  if (auth === 'checking' || !boot.data) return <Splash text={boot.isError ? 'Couldn’t load your home. Retrying…' : ''} />;
  if (!boot.data.preferences || !boot.data.device) {
    return <Splash text="You’re signed in as a family member. This page is for the home display — open /app instead." />;
  }
  return <DisplayShell boot={boot.data} prefs={boot.data.preferences} />;
}

const GRADIENT = 'linear-gradient(155deg,#16408F 0%,#2F6CC0 40%,#4C93D3 68%,#2A8C8C 100%)';

/**
 * While the page's animated launch splash (index.html) is up it covers this and shows the same
 * message; this still copy is what remains if the app lands here after that splash has gone.
 */
function Splash({ text }: { text: string }) {
  useEffect(() => setSplashMessage(text), [text]);
  return (
    <div className="obv-static flex h-full flex-col items-center justify-center text-center" style={{ background: GRADIENT, color: '#fff' }}>
      <div className="obv-name" aria-label="Our Beach Villa">
        <span className="obv-word">Our</span>
        <span className="obv-word">Beach</span>
        <span className="obv-word">Villa</span>
      </div>
      <svg className="obv-wave" width="260" height="26" viewBox="0 0 260 26" aria-hidden>
        <path d="M4 10 C 24 2, 44 2, 64 10 S 104 18, 124 10 S 164 2, 184 10 S 224 18, 256 10" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="2" strokeLinecap="round" />
        <path d="M34 20 C 54 13, 74 13, 94 20 S 134 27, 154 20 S 194 13, 226 20" fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
      {text ? <p className="obv-msg">{text}</p> : null}
    </div>
  );
}
